import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Users, Flame, Thermometer, Snowflake, TrendingUp, ArrowRight,
  Play, Loader2, UserPlus, RefreshCw, Heart, Crosshair,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface Prospect {
  first_name: string;
  last_name: string;
  email: string | null;
  company: string | null;
  sector: string | null;
  score: number;
  status: string;
  funnel_step: string;
  source_agent: string | null;
  source_channel: string | null;
  interactions_count: number;
  created_at: string;
}

const STATUS_CONFIG = {
  chaud: { label: "Chaud", icon: Flame, color: "text-red-400", bg: "bg-red-500/10 border-red-500/20" },
  tiede: { label: "Tiède", icon: Thermometer, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
  froid: { label: "Froid", icon: Snowflake, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" },
  converti: { label: "Converti", icon: TrendingUp, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
};

const FUNNEL_STEPS = [
  { key: "hook", label: "Hook", emoji: "🎣" },
  { key: "qualification", label: "Qualification", emoji: "🎯" },
  { key: "diagnostic", label: "Diagnostic", emoji: "📊" },
  { key: "offre", label: "Offre", emoji: "💎" },
];

export function AcquisitionPipeline() {
  const { user } = useAuth();
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningAction, setRunningAction] = useState<string | null>(null);

  const fetchProspects = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API_URL}/api/nanocorp/pipeline/prospects`, { credentials: "include" as RequestCredentials });
      if (res.ok) {
        const data = await res.json();
        setProspects(data?.prospects || []);
      }
    } catch {}
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);

  const runAction = async (agentId: string, action: string, label: string) => {
    if (runningAction) return;
    setRunningAction(action);
    try {
      const res = await fetch(`${API_URL}/api/nanocorp/run/${agentId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include" as RequestCredentials,
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`${label} terminé`);
      await fetchProspects();
    } catch (err: any) {
      toast.error(`Erreur: ${err.message}`);
    } finally {
      setRunningAction(null);
    }
  };

  const byStatus = {
    chaud: prospects.filter(p => p.status === "chaud"),
    tiede: prospects.filter(p => p.status === "tiede"),
    froid: prospects.filter(p => p.status === "froid"),
    converti: prospects.filter(p => p.status === "converti"),
  };

  const byFunnel = FUNNEL_STEPS.map(step => ({
    ...step,
    count: prospects.filter(p => p.funnel_step === step.key).length,
  }));

  const avgScore = prospects.length > 0 ? Math.round(prospects.reduce((s, p) => s + p.score, 0) / prospects.length) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Actions rapides */}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm" variant="outline" className="gap-1.5"
          onClick={() => runAction("prospecteur", "run_prospection", "Prospection")}
          disabled={!!runningAction}
        >
          {runningAction === "run_prospection" ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />}
          Chasser des leads
        </Button>
        <Button
          size="sm" variant="outline" className="gap-1.5"
          onClick={() => runAction("qualificateur", "run_scoring", "Scoring")}
          disabled={!!runningAction}
        >
          {runningAction === "run_scoring" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Crosshair className="h-3 w-3" />}
          Scorer les prospects
        </Button>
        <Button
          size="sm" variant="outline" className="gap-1.5"
          onClick={() => runAction("nurturing", "run_nurturing", "Nurturing")}
          disabled={!!runningAction}
        >
          {runningAction === "run_nurturing" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Heart className="h-3 w-3" />}
          Lancer le nurturing
        </Button>
        <Button
          size="sm" variant="ghost" className="gap-1.5"
          onClick={fetchProspects}
        >
          <RefreshCw className="h-3 w-3" /> Actualiser
        </Button>
      </div>

      {/* Stats pipeline */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="border-border/50">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-muted-foreground text-[10px] mb-1">
              <Users className="h-3 w-3" /> Total
            </div>
            <p className="text-xl font-bold text-foreground">{prospects.length}</p>
          </CardContent>
        </Card>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          const count = byStatus[key as keyof typeof byStatus].length;
          return (
            <Card key={key} className="border-border/50">
              <CardContent className="p-3">
                <div className={`flex items-center gap-2 text-[10px] mb-1 ${config.color}`}>
                  <Icon className="h-3 w-3" /> {config.label}
                </div>
                <p className="text-xl font-bold text-foreground">{count}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tunnel visuel */}
      <Card className="border-border/50">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Tunnel d'acquisition</h3>
          <div className="flex items-center gap-1">
            {byFunnel.map((step, i) => {
              const width = prospects.length > 0 ? Math.max((step.count / prospects.length) * 100, 8) : 25;
              return (
                <div key={step.key} className="flex items-center flex-1">
                  <div
                    className="rounded-lg p-3 text-center flex-1 border border-border/30 transition-all hover:border-primary/30"
                    style={{
                      background: `hsl(var(--primary) / ${0.05 + (i * 0.08)})`,
                    }}
                  >
                    <p className="text-lg mb-0.5">{step.emoji}</p>
                    <p className="text-2xl font-bold text-foreground">{step.count}</p>
                    <p className="text-[10px] text-muted-foreground">{step.label}</p>
                  </div>
                  {i < byFunnel.length - 1 && (
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0 mx-1" />
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <p className="text-[10px] text-muted-foreground">Score moyen</p>
            <Progress value={avgScore} className="flex-1 h-1.5" />
            <p className="text-xs font-semibold text-foreground">{avgScore}/100</p>
          </div>
        </CardContent>
      </Card>

      {/* Liste des prospects par statut */}
      {(["chaud", "tiede", "froid"] as const).map(status => {
        const config = STATUS_CONFIG[status];
        const Icon = config.icon;
        const list = byStatus[status];
        if (list.length === 0) return null;

        return (
          <div key={status}>
            <div className="flex items-center gap-2 mb-2">
              <Icon className={`h-4 w-4 ${config.color}`} />
              <h3 className="text-sm font-semibold text-foreground">{config.label}</h3>
              <Badge variant="outline" className="text-[10px]">{list.length}</Badge>
            </div>
            <div className="grid gap-2">
              {list.slice(0, 10).map(prospect => (
                <Card key={prospect.id} className={`border ${config.bg}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground truncate">
                            {prospect.first_name} {prospect.last_name}
                          </p>
                          <Badge variant="outline" className="text-[9px] shrink-0">
                            {prospect.score}/100
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          {prospect.company && (
                            <p className="text-[10px] text-muted-foreground">{prospect.company}</p>
                          )}
                          {prospect.source_agent && (
                            <p className="text-[10px] text-primary">via {prospect.source_agent}</p>
                          )}
                          {prospect.source_channel && (
                            <p className="text-[10px] text-muted-foreground">• {prospect.source_channel}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <Badge variant="outline" className="text-[9px]">
                          {FUNNEL_STEPS.find(s => s.key === prospect.funnel_step)?.emoji} {prospect.funnel_step}
                        </Badge>
                        <p className="text-[9px] text-muted-foreground mt-0.5">
                          {prospect.interactions_count} interaction{prospect.interactions_count > 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {prospects.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">Aucun prospect dans le pipeline</p>
          <p className="text-xs mt-1">Lancez l'agent Prospecteur pour commencer à remplir votre tunnel</p>
        </div>
      )}
    </div>
  );
}
